import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useSelector, useDispatch } from "react-redux";
import { fetchData } from "actions/actions";

// -----------------------------
// Date helpers (English date strings + ranges)
// -----------------------------
const normalize = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
const toDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const stripOrdinal = (s) => (s || "").replace(/(\d+)(st|nd|rd|th)/gi, "$1");

// Supports:
// - "April 30, 2025"
// - "February 18th 2026"
// - "February 18th 2026 - February 20th 2026"
const parseEnglishStartDate = (dateStr) => {
  if (!dateStr) return null;
  const left = dateStr.split("-")[0]?.trim() || dateStr.trim();
  const cleaned = stripOrdinal(left);
  const d = new Date(cleaned);
  return Number.isNaN(d.getTime()) ? null : toDay(d);
};

export default function TTESeminarAdvancedFr() {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  // redux source
  const dataS = useSelector((state) => state.data?.data?.seminarFr);
  const data = useMemo(() => {
     return Array.isArray(dataS) ? [...dataS].reverse() : [];
   }, [dataS]);
    

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  // -----------------------------
  // Theme / look (same as advanced TEM)
  // -----------------------------
  const accent = "#DC242C";
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const shadow = useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none");

  // Date pill base colors
  const pillBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const pillText = useColorModeValue("gray.700", "gray.200");

  // -----------------------------
  // Modal
  // -----------------------------
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedSeminar, setSelectedSeminar] = useState(null);

  const openSeminar = (seminar) => {
    setSelectedSeminar(seminar);
    onOpen();
  };

  // -----------------------------
  // Controls: search + date filter + sort + load more
  // -----------------------------
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState(""); // YYYY-MM-DD
  const [sortMode, setSortMode] = useState("newest"); // newest | oldest | title

  const PAGE_SIZE = isMobile ? 4 : 6;
  const [visible, setVisible] = useState(PAGE_SIZE);

  const seminarsTTE = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((s) => s?.type === "TTE");
  }, [data]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    const from = fromDate ? toDay(fromDate) : null;
    const to = toDate ? toDay(toDate) : null;

    let out = seminarsTTE.filter((s) => {
      const title = s?.titleFr || "";
      const address = s?.address || "";
      const date = s?.dateFr || "";

      if (q) {
        const hay = normalize([title, address, date].join(" "));
        if (!hay.includes(q)) return false;
      }

      const sd = parseEnglishStartDate(date);
      if ((from || to) && !sd) return false;
      if (from && sd < from) return false;
      if (to && sd > to) return false;

      return true;
    });

    out.sort((a, b) => {
      if (sortMode === "title") return (a.title || "").localeCompare(b.title || "");
      const da = parseEnglishStartDate(a?.date)?.getTime() ?? 0;
      const db = parseEnglishStartDate(b?.date)?.getTime() ?? 0;
      return sortMode === "oldest" ? da - db : db - da;
    });

    return out;
  }, [seminarsTTE, query, fromDate, toDate, sortMode]);

  const today = toDay(new Date());
  const isUpcoming = (dateStr) => {
    const sd = parseEnglishStartDate(dateStr);
    if (!sd) return false;
    return sd >= today;
  };

  // reset pagination when filters change
  useEffect(() => {
    setVisible(PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, fromDate, toDate, sortMode, isMobile]);

  // -----------------------------
  // Render
  // -----------------------------
  if (!data) {
    return (
      <Flex minH="50vh" align="center" justify="center">
        Chargement...
      </Flex>
    );
  }

  return (
    <Box pt={{ base: 2, md: 4 }} pb={{ base: 10, md: 12 }}>
      <Container maxW="1200px">
        {/* Header */}
        <Stack spacing={2} mb={6}>
          <Heading fontSize={{ base: "xl", md: "2xl" }} color={titleColor} letterSpacing="tight">
            TTE Séminaires
          </Heading>
          <Text color={subColor}>Consultez la liste des séminaires TTE. Cliquez sur une carte pour voir les détails.</Text>
        </Stack>

        {/* Controls */}
        <Box
          border="1px solid"
          borderColor={border}
          bg={panelBg}
          borderRadius="22px"
          p={{ base: 4, md: 5 }}
          mb={6}
        >
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} alignItems="center">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={useColorModeValue("gray.500", "gray.400")} />
              </InputLeftElement>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title, address, date…"
                borderRadius="14px"
              />
            </InputGroup>

            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              borderRadius="14px"
              title="From"
            />

            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              borderRadius="14px"
              title="To"
            />

            <Input
              as="select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              borderRadius="14px"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="title">Title (A–Z)</option>
            </Input>
          </SimpleGrid>

          <HStack mt={4} spacing={3} flexWrap="wrap">
            <Tag borderRadius="full" fontWeight="700">
              Showing {Math.min(visible, filtered.length)} / {filtered.length}
            </Tag>

            {(query || fromDate || toDate) && (
              <Button
                size="sm"
                variant="ghost"
                fontWeight="700"
                onClick={() => {
                  setQuery("");
                  setFromDate("");
                  setToDate("");
                }}
              >
                Clear filters
              </Button>
            )}
          </HStack>
        </Box>

        {/* Empty state */}
        {filtered.length === 0 ? (
          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="22px"
            p={10}
            textAlign="center"
            bg={panelBg}
          >
            <Text fontWeight="800" color={titleColor}>
              Exciting Seminars Coming Soon, Stay Tuned!
            </Text>
            <Text color={subColor} mt={2}>
              Try clearing filters or searching different keywords.
            </Text>
          </Box>
        ) : (
          <>
            {/* Cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {filtered.slice(0, visible).map((s, idx) => {
                const upcoming = isUpcoming(s?.date);
                const clickable = Boolean(s?.imageLink);

                return (
                  <Box
                    key={s?.id ?? `${s?.title ?? "seminar"}-${idx}`}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : -1}
                    onClick={() => clickable && openSeminar(s)}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") openSeminar(s);
                    }}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={border}
                    borderRadius="22px"
                    p={{ base: 5, md: 6 }}
                    textAlign="left"
                    boxShadow={shadow}
                    transition="all 180ms ease"
                    _hover={clickable ? { transform: "translateY(-2px)" } : undefined}
                    opacity={clickable ? 1 : 0.92}
                    cursor={clickable ? "pointer" : "default"}
                  >
                    {/* Top tags */}
                    <HStack spacing={2} flexWrap="wrap">
                      <Tag
                        size="sm"
                        borderRadius="full"
                        bg={useColorModeValue("rgba(49,83,105,0.08)", "rgba(255,255,255,0.08)")}
                        fontWeight="700"
                        color={titleColor}
                      >
                        TTE
                      </Tag>

                      {upcoming && (
                        <Tag size="sm" borderRadius="full" bg={accent} color="white" fontWeight="700">
                          À venir
                        </Tag>
                      )}

                      {!clickable && (
                        <Tag
                          size="sm"
                          borderRadius="full"
                          bg={useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.10)")}
                          color={subColor}
                          fontWeight="700"
                        >
                          No poster
                        </Tag>
                      )}
                    </HStack>

                    {/* Title */}
                    <Text mt={3} fontWeight="900" color={titleColor} fontSize="lg" lineHeight={1.2}>
                      {s?.titleFr}
                    </Text>

                    {/* ✅ Date pill (your screenshot style) */}
                    <Box mt={3}>
                      <Tag
                        borderRadius="full"
                        px={4}
                        py={1}
                        fontSize="sm"
                        fontWeight="600"
                        bg={upcoming ? "rgba(220,36,44,0.12)" : pillBg}
                        color={upcoming ? accent : pillText}
                      >
                        {s?.dateFr}
                      </Tag>
                    </Box>

                    <Divider my={4} borderColor={border} />

                    {/* Address (NOT bold) */}
                    <Text color={subColor}>{s?.address}</Text>

                    {clickable && (
                      <Text
                        mt={4}
                        fontSize="sm"
                        color={useColorModeValue("blue.600", "blue.300")}
                        fontStyle="italic"
                      >
                        Cliquez ici pour plus d'informations
                      </Text>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>

            {/* Load more */}
            {visible < filtered.length && (
              <Flex justify="center" mt={8}>
                <Button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  bg={accent}
                  color="white"
                  fontWeight="800"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Load more
                </Button>
              </Flex>
            )}
          </>
        )}
      </Container>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" allowPinchZoom>
        <ModalOverlay />
        <ModalContent>
          {selectedSeminar && (
            <>
              <ModalHeader>À propos de notre hôte</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {selectedSeminar.imageLink ? (
                  <Image
                    src={selectedSeminar.imageLink}
                    alt={selectedSeminar.titleFr}
                    width="100%"
                    objectFit="contain"
                  />
                ) : (
                  <Text>No poster available.</Text>
                )}
              </ModalBody>
              <ModalFooter>
                <Button bg={accent} color="white" fontWeight="800" onClick={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
}
